import React, { useCallback, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ContainerBox from 'components/ContainerBox';
import ProgressWithLabel from './ProgressWithLabel';
import TranslatableItem from './TranslatableItem';
import { ById, Translatable } from 'common/types';
import { getTranslationPercentageMany } from 'common/modules/translations';
import Container from 'components/Container';
import { omit, sortBy } from 'lodash';
import SaveStatus from 'components/SaveStatus';
import { RiCloseLine } from 'react-icons/ri';
import { styled } from '@mui/system';
import { useSelector, useDispatch } from 'react-redux';
import * as TranslationsSelectors from 'selectors/TranslationsSelectors';
import * as TranslationsActions from 'actions/TranslationsActions';
import { TranslationGroup } from 'reducers/TranslationsReducer';

interface Props {
	title: string;
	translatablesById: ById<Translatable>;
	group: TranslationGroup;
	onClose?: () => void;
}
const TranslatableGroup = (props: Props) => {
	const { title, group, translatablesById } = props;

	const dispatch = useDispatch();
	const isSaving = useSelector(TranslationsSelectors.isSaving);
	const isError = useSelector(TranslationsSelectors.isError);
	const activeLanguage = useSelector(TranslationsSelectors.activeLanguage);

	const translatables = sortBy(Object.values(translatablesById), (item) => item.label);
	const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});

	const handleExpandedChange = useCallback((id: string, expanded: boolean) => {
		setExpandedItems((prev) => {
			return expanded
				? {
						...prev,
						[id]: true,
				  }
				: omit(prev, id);
		});
	}, []);

	return (
		<Box>
			<DrawerHeader>
				<Container noVerticalPadding>
					<DrawerHeaderContent>
						<IconButton onClick={props.onClose}>
							<RiCloseLine />
						</IconButton>
						<Box sx={{ flex: '1', ml: 2 }}>
							<Typography variant="h5" sx={{ fontSize: '2.2rem' }}>
								{title}
							</Typography>
							<Box sx={{ width: '100%', maxWidth: 300, mt: 1 }}>
								<ProgressWithLabel
									value={getTranslationPercentageMany(translatables, activeLanguage).percentage}
								/>
							</Box>
						</Box>
						<SaveStatus
							status={isSaving ? 'saving' : isError ? 'not-saved' : 'saved'}
							onRetry={() => dispatch(TranslationsActions.retryAllFailedSaves())}
						/>
					</DrawerHeaderContent>
				</Container>
			</DrawerHeader>
			<Container>
				<ContainerBox padding="none">
					<Box>
						{translatables.map((translatable) => (
							<TranslatableItem
								key={translatable.id}
								group={group}
								data={translatable}
								expanded={expandedItems[translatable.id] ?? false}
								onExpandedChange={handleExpandedChange}
							/>
						))}
					</Box>
				</ContainerBox>
			</Container>
		</Box>
	);
};

const DrawerHeader = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	borderBottom: `1px solid ${theme.palette.border.paper}`,
	background: theme.palette.background.paper,
	position: 'sticky',
	top: 0,
	zIndex: 10,
	height: 90,
}));

const DrawerHeaderContent = styled(Box)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	flexWrap: 'wrap',
	justifyContent: 'space-between',
	height: '100%',
});

export default TranslatableGroup;
